// バーコード／SKU からの商品解決 — Stocky parity v2 Step 4。
// USB/BT のバーコードスキャナは「文字列 + Enter」をキーボードとして打つだけなので、
// 読み取った文字列をそのまま backend へ渡して barcode → SKU の順に照合させる。
//
// 画面が持っている商品一覧（/api/inventory-adjustments/variants など）で引かないのは、
// あの一覧が 500 件で頭打ちなのに Pro は商品 10,000 件まで使えるため＝棚にある物を
// 打つスキャンは一覧の外を引きに来る。
import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

import { t } from "../lib/i18n";
import { displayVariantTitle } from "../utils/productTitles";

export interface VariantMatch {
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  barcode: string | null;
  title: string | null;
  product_title: string | null;
}

export interface VariantLookupResult {
  code: string;
  /** どちらの列で当たったか。SKU 一致だったのに「バーコードで解決」と見えると、
   *  バーコード未設定に気づけないので画面に出す。 */
  matched_by: "barcode" | "sku";
  variants: VariantMatch[];
}

/** 商品名の表示（一覧・候補・確定表示で同じ見え方にする）。 */
export function variantMatchLabel(variant: VariantMatch): string {
  const name = displayVariantTitle(variant.product_title, variant.title);
  const sku = variant.sku ? `（SKU: ${variant.sku}）` : "";
  return `${name || t("(名称未設定)")}${sku}`;
}

export async function lookupVariantByCode(
  code: string,
): Promise<VariantLookupResult> {
  const result = await apiFetch<VariantLookupResult>(
    `/api/variants/lookup?code=${encodeURIComponent(code)}`,
  );
  if (!Array.isArray(result.variants)) {
    throw new TypeError("variant lookup response is invalid");
  }
  return {
    ...result,
    variants: result.variants.map((variant) => ({
      ...variant,
      shopify_variant_id: canonicalStockyPublicBigIntId(
        variant.shopify_variant_id,
      ),
    })),
  };
}

export interface VariantVolumeResult {
  variant_id: string;
  unit_volume_m3: string | null;
}

/**
 * 1個あたりの容積（m³・CBM）を設定/クリアする。読み手は諸掛の容積比按分だけ。
 * null＝「未入力」へ戻す（0＝容積ゼロと明示、とは意味が違う）。
 * Shopify に対応フィールドが無いため同期では消えない（cost_override と同じ扱い）。
 */
export async function setVariantVolume(
  variantId: StockyPublicBigIntId,
  unitVolumeM3: string | null,
): Promise<VariantVolumeResult> {
  const result = await apiFetch<
    Omit<VariantVolumeResult, "variant_id"> & { variant_id: unknown }
  >(`/api/variants/${canonicalStockyPublicBigIntId(variantId)}/volume`, {
    method: "PATCH",
    body: { unit_volume_m3: unitVolumeM3 },
  });
  return {
    ...result,
    variant_id: canonicalStockyPublicBigIntId(result.variant_id),
  };
}
